import { useState, useEffect } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, ChevronDown, Search } from 'lucide-react'
import { Icon } from '../assets/icons/Icon'
import { useAuth } from '../lib/auth'
import { useAccount } from '../lib/account'
import { useBranding } from '../lib/branding'
import { useTerminology } from '../lib/terminology'
import { isDark, setTheme as applyThemeChoice } from '../lib/theme'
import { PLAN_ORDER, getRequiredPlan } from '../lib/planGate'
import { NAV_GROUPS } from './nav-config'
import ProfileModal from './ProfileModal'

const COLLAPSED_GROUPS_KEY = 'crewos-sidebar-collapsed-groups'

/** @returns {Set<string>} */
function loadCollapsedGroups() {
  try {
    const raw = localStorage.getItem(COLLAPSED_GROUPS_KEY)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    return new Set(Array.isArray(parsed) ? parsed : [])
  } catch {
    return new Set()
  }
}

/**
 * @typedef {import('./nav-config').NavLink} NavLinkDef
 *
 * @param {{
 *   collapsed: boolean,
 *   setCollapsed: (v: boolean) => void,
 *   links: NavLinkDef[],
 *   chatOpen: boolean,
 *   setChatOpen: (v: boolean) => void,
 *   canUseAi: boolean,
 *   onOpenPalette: () => void,
 * }} props
 */
export default function DesktopSidebar({ collapsed, setCollapsed, links, chatOpen, setChatOpen, canUseAi, onOpenPalette }) {
  const hotkeyLabel = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl K'
  const { profile, signOut, isOwner } = useAuth()
  const branding = useBranding()
  const { plan } = useAccount()
  const navigate = useNavigate()
  const [showProfile, setShowProfile] = useState(false)
  const [dark, setDark] = useState(isDark())
  const [collapsedGroups, setCollapsedGroups] = useState(loadCollapsedGroups)
  const t = useTerminology()

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_GROUPS_KEY, JSON.stringify([...collapsedGroups]))
    } catch {
      /* swallow quota/storage errors */
    }
  }, [collapsedGroups])

  function toggleTheme() {
    const next = dark ? 'light' : 'dark'
    applyThemeChoice(next)
    setDark(!dark)
  }

  /** @param {string} key */
  function toggleGroup(key) {
    setCollapsedGroups(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const settingsLink = links.find(l => l.group === 'settings')
  const groupedLinks = NAV_GROUPS.map(g => ({
    ...g,
    items: links.filter(l => l.group === g.key),
  })).filter(g => g.items.length > 0)

  /** @param {import('./nav-config').NavLink} link */
  function renderLink(link) {
    const required = getRequiredPlan(link.gate)
    const locked = required !== null && PLAN_ORDER[plan] < PLAN_ORDER[required]
    const displayLabel = (link.labelKey && t[link.labelKey]) || link.label
    return (
      <NavLink
        key={link.to}
        to={link.to}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${locked ? 'opacity-50' : ''} ${
            isActive
              ? 'bg-brand-100 text-brand-900 font-semibold'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`
        }
      >
        <div className="relative shrink-0">
          <Icon name={link.icon} size={18} />
          {locked && <Icon name="status-locked" size={10} className="absolute -top-1 -right-1 text-gray-400" />}
        </div>
        {!collapsed && <span className="truncate">{displayLabel}</span>}
      </NavLink>
    )
  }

  return (
    <aside className={`hidden md:flex fixed top-0 left-0 h-screen bg-white flex-col transition-all duration-200 z-50 border-r border-gray-200 ${collapsed ? 'w-16' : 'w-56'}`}>
      <div className={`flex items-center h-20 border-b border-gray-100 ${collapsed ? 'px-2 justify-center' : 'px-3'}`}>
        {!collapsed ? (
          <img src={branding.logo_url} alt={branding.org_name} className="h-14 w-auto object-contain" />
        ) : (
          <div className="w-10 h-10 bg-brand-300 rounded-full flex items-center justify-center">
            <span className="text-brand-900 text-[10px] font-bold">{branding.org_name.substring(0, 2).toUpperCase()}</span>
          </div>
        )}
        <div className={`flex items-center gap-1 ${collapsed ? '' : 'ml-auto'}`}>
          {canUseAi ? (
            <button onClick={() => setChatOpen(!chatOpen)} className={`p-1.5 rounded-lg transition-colors ${chatOpen ? 'text-brand-700 bg-brand-100' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`} title="AI Assistant">
              <Icon name="chrome-ai-chat" size={16} />
            </button>
          ) : (
            <button onClick={() => navigate('/settings?tab=billing')} className="p-1.5 rounded-lg text-gray-300 hover:bg-gray-100 relative" title="AI Assistant — Upgrade to Pro">
              <Icon name="chrome-ai-chat" size={16} />
              <Icon name="status-locked" size={8} className="absolute -top-0.5 -right-0.5 text-gray-400" />
            </button>
          )}
          <button onClick={() => setCollapsed(!collapsed)} className="text-gray-400 hover:text-gray-700 p-1.5">
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>
      </div>
      {/* Command-palette trigger */}
      <div className={`px-2 pt-3 pb-1 ${collapsed ? '' : ''}`}>
        <button
          type="button"
          onClick={onOpenPalette}
          title={`Jump to… (${hotkeyLabel})`}
          className={`w-full flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 hover:bg-white hover:border-gray-300 transition-colors ${collapsed ? 'justify-center py-2' : 'px-3 py-2'}`}
        >
          <Search size={14} className="text-gray-400 shrink-0" />
          {!collapsed && (
            <>
              <span className="flex-1 text-left text-xs text-gray-500">Jump to…</span>
              <kbd className="text-[10px] font-mono text-gray-400 bg-white border border-gray-200 rounded px-1 py-0.5 shrink-0">{hotkeyLabel}</kbd>
            </>
          )}
        </button>
      </div>
      <nav className="flex-1 py-3 px-2 overflow-y-auto">
        {collapsed ? (
          <div className="space-y-0.5">
            {groupedLinks.flatMap(g => g.items).map(renderLink)}
            {settingsLink && (
              <>
                <div className="my-2 mx-1 border-t border-gray-100" />
                {renderLink(settingsLink)}
              </>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {groupedLinks.map(group => {
              const isOpen = !collapsedGroups.has(group.key)
              return (
                <div key={group.key}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.key)}
                    className="w-full flex items-center justify-between px-3 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-600"
                  >
                    <span>{group.label}</span>
                    <ChevronDown size={12} className={`transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                  </button>
                  {isOpen && (
                    <div className="space-y-0.5">
                      {group.items.map(renderLink)}
                    </div>
                  )}
                </div>
              )
            })}
            {settingsLink && (
              <div className="pt-3 border-t border-gray-100 space-y-0.5">
                {renderLink(settingsLink)}
              </div>
            )}
          </div>
        )}
      </nav>
      <div className={`py-3 border-t border-gray-100 ${collapsed ? 'px-2' : 'px-3'}`}>
        {!collapsed ? (
          <div className="flex items-center justify-between">
            <button onClick={() => setShowProfile(true)} className="min-w-0 text-left hover:bg-gray-50 rounded-lg px-1.5 py-1 -mx-1.5 transition-colors" title="Profile settings">
              <div className="text-sm font-medium text-gray-700 truncate">{profile?.display_name || 'User'}</div>
              <div className="text-xs text-gray-400 truncate">
                {isOwner ? 'Owner' : 'Operator'} · v0.4.0
              </div>
            </button>
            <button onClick={toggleTheme} className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 shrink-0" title={dark ? 'Light mode' : 'Dark mode'}>
              {dark ? <Icon name="chrome-theme-light" size={16} /> : <Icon name="chrome-theme-dark" size={16} />}
            </button>
            <button onClick={signOut} className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 shrink-0" title="Sign out">
              <Icon name="chrome-signout" size={16} />
            </button>
          </div>
        ) : (
          <div className="space-y-1">
            <button onClick={() => setShowProfile(true)} className="w-full flex justify-center text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100" title="Profile settings">
              <Icon name="nav-settings" size={16} />
            </button>
            <button onClick={toggleTheme} className="w-full flex justify-center text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100" title={dark ? 'Light mode' : 'Dark mode'}>
              {dark ? <Icon name="chrome-theme-light" size={16} /> : <Icon name="chrome-theme-dark" size={16} />}
            </button>
            <button onClick={signOut} className="w-full flex justify-center text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100" title="Sign out">
              <Icon name="chrome-signout" size={16} />
            </button>
          </div>
        )}
      </div>
      <ProfileModal open={showProfile} onClose={() => setShowProfile(false)} />
    </aside>
  )
}
